import { useCallback } from 'react';
import styled from 'styled-components';
import {
  createReadableClass,
  getHandleActionKeyDown
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { Position, PropsWithClassName } from '../../../../../../types';
import { MOBILE_WIDTH } from '../../../../../constants';
import {
  MINIMIZE_PLAYER_POSITION_ADJUST,
  positionAdjustMinimizePlayer
} from '../../../../../helpers';
import {
  Deactivation,
  FREE_LINK_HEIGHT,
  StyledFreeLink,
  TrackInfo,
  useTheme
} from '../../../../common';
import { useSource } from '../../../../sources';
import { usePlayer } from '../../../PlayerProvider';
import { CloseButton, MinimizeButton } from '../../../player-parts';
import { useFloating } from '../FloatingProvider';
import { MinimizePlayerPlayPause } from './MinimizePlayerPlayPause';

const PADDING = 8;
const MIN_WIDTH = 200;

export function MinimizedPlayer({
  className,
  isMobileView = false
}: PropsWithClassName & { isMobileView?: boolean }) {
  const {
    position,
    closeButton,
    playerParts,
    font,
    displayDeactivation,
    displayFreeLink
  } = useSettings();
  const { currentItem } = useSource();
  const { showPlayer } = usePlayer();
  const { setMinimize, minimizePlayer: isPlayerMinimized } = useFloating();
  const { backgroundStyle, textColor } = useTheme();

  const { hasAuthor, hasTitle } = currentItem ?? {};

  const minimizePlayer = useCallback(() => {
    setMinimize(false);
  }, [setMinimize]);

  if (!showPlayer) {
    return null;
  }

  const hasStatus = playerParts.includes('status');
  const withMinimizeButton = !hasTitle && !hasAuthor && !hasStatus;

  return (
    <Deactivation
      displayDeactivation={displayDeactivation && isPlayerMinimized}
    >
      <StyledPlayer
        _position={position}
        _backgroundStyle={backgroundStyle}
        _font={font?.value || 'inherit'}
        _withStatus={hasStatus}
        _color={textColor}
        _displayFreeLink={!!displayFreeLink}
        className={className}
      >
        <MinimizePlayerPlayPause />

        <StyledTrackInfo
          tabIndex={0}
          role="button"
          onClick={minimizePlayer}
          onKeyDown={getHandleActionKeyDown(minimizePlayer)}
          titleTextStyle={{
            fontSize: 13,
            fontWeight: 'bold',
            lineHeight: '16px'
          }}
          authorTextStyle={{
            fontSize: 11,
            lineHeight: '12px'
          }}
        />

        {withMinimizeButton && (
          <MinimizeButton
            styles="margin-left: 12px;"
            iconSize={24}
            size={32}
            flexSize={32}
            withoutMargin
            revert
          />
        )}

        {closeButton && <CloseButton withoutMargin />}

        {!isMobileView && <StyledFreeLink />}
      </StyledPlayer>
    </Deactivation>
  );
}

const StyledTrackInfo = styled(TrackInfo).attrs({
  className: createReadableClass({
    component: 'minimized-player',
    element: 'track-info'
  })
})`
  cursor: pointer;
  margin-left: 4px;
`;

const StyledPlayer = styled.div.attrs<{
  _font: string;
  _position: Position;
  _backgroundStyle: string;
  _withStatus: boolean;
  _color: string;
  _displayFreeLink: boolean;
}>({
  className: createReadableClass({
    component: 'minimized-player'
  })
})`
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 8px;
  border-radius: 12px;
  gap: 8px;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.15);
  color: ${({ _color }) => _color};
  min-width: ${MIN_WIDTH - PADDING * 2}px;
  max-width: calc(
    ${({ _withStatus }) => (_withStatus ? 240 : MIN_WIDTH)}px - ${PADDING * 2}px
  );
  font-family: ${({ _font }) => _font};

  ${({ _backgroundStyle }) => _backgroundStyle}

  ${({ _position, _displayFreeLink }) => `
    top: ${positionAdjustMinimizePlayer(_position, 'top')};
    bottom: ${positionAdjustMinimizePlayer(_position, 'bottom', _displayFreeLink ? FREE_LINK_HEIGHT + MINIMIZE_PLAYER_POSITION_ADJUST : MINIMIZE_PLAYER_POSITION_ADJUST)};
    left: ${positionAdjustMinimizePlayer(_position, 'left')};
    right: ${positionAdjustMinimizePlayer(_position, 'right')};
  `}

  @media (max-width: ${MOBILE_WIDTH}px) {
    max-width: none;
    padding: 4px 8px;
    top: unset;
    bottom: 0;
    right: 0;
    left: 0;
    width: 100vw;
    box-sizing: border-box;
    border-radius: 0;
  }
`;
